<template>
  <div class="w-full h-full relative flex flex-col overflow-auto bg-primary-50">
    <!-- Back Button -->
    <div class="fixed top-4 w-full z-10">
      <div class="mobile-status-padding"></div>
      <button
        @click="closeDialog"
        class="relative top-0 left-4 w-10 h-10 rounded-full bg-white shadow flex items-center justify-center"
      >
        <svg
          class="w-5 h-5 text-slate-700"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
    </div>

    <div class="h-full w-full fixed top-0 left-0">
      <Map ref="mapRef" hide-user-position @ready="onMapReady" />
    </div>
    <div class="flex-1 px-4 pb-8 mt-[60vh] mobile-status-padding">
      <!-- Banner alerte pour Échec ou Annulation -->
      <div
        v-if="isOrderCancelled || isPaymentFailed"
        class="liquidGlassCard p-4 rounded-3xl mb-4 bg-red-50 border border-red-200 flex items-center gap-3 text-red-700"
      >
        <div
          class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0"
        >
          <svg
            class="w-6 h-6 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <div>
          <div class="font-bold text-base">
            {{ isPaymentFailed ? 'Échec du paiement' : 'Commande annulée' }}
          </div>
          <div class="text-xs opacity-90">
            {{ failureReasonMessage }}
          </div>
        </div>
      </div>

      <!-- Order Info Card -->
      <div
        class="liquidGlassCard p-6 rounded-3xl mb-4 overflow-hidden relative"
      >
        <div
          class="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 blur-3xl"
        ></div>

        <div class="grid grid-cols-2 justify-between items-end mb-6 relative">
          <div>
            <div class="text-md font-bold title-color line-clamp-2">
              {{
                orderData?.pickupLabel ||
                orderData?.restaurantName ||
                'Restaurant'
              }}
            </div>
            <div class="text-xs opacity-70 title-color line-clamp-2">
              {{ orderData?.pickupAddress || '-' }}
            </div>
            <div class="text-sm title-color line-clamp-2 font-bold">
              {{ formatDate(orderData?.createdAt) }}
            </div>
          </div>
          <div class="text-right">
            <div class="text-2xl font-bold title-color">
              {{
                formatPrice(
                  orderData?.pricing?.total || orderData?.totalPrice || 0
                )
              }}
            </div>
            <div class="text-xs opacity title-color">
              Commande <br />
              <span class="uppercase font-bold">
                #{{
                  orderData?.reference || orderData?.orderNumber || '-'
                }}</span
              >
            </div>
          </div>
        </div>
        <div class="border-t border-slate-100 pt-6">
          <div
            class="rounded-2xl p-4 flex items-center justify-between"
            :class="
              isOrderCancelled || isPaymentFailed
                ? 'bg-red-50'
                : 'bg-primary/10'
            "
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-full flex items-center justify-center"
                :class="
                  isOrderCancelled || isPaymentFailed
                    ? 'bg-red-500'
                    : 'bg-primary'
                "
              >
                <svg
                  class="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <div class="text-sm font-bold text-slate-800">Livraison</div>
                <div class="text-xs text-slate-500">
                  {{
                    isOrderCancelled ||
                    isPaymentFailed ||
                    orderData?.status == 'delivered' ||
                    orderData?.status == 'arrived'
                      ? 'Statut de la commande'
                      : 'Préparation & Livraison'
                  }}
                </div>
              </div>
            </div>
            <div
              class="text-xl font-black min-w-[80px] text-right"
              :class="
                isOrderCancelled || isPaymentFailed
                  ? 'text-red-600'
                  : 'text-primary'
              "
            >
              <template v-if="orderData?.status == 'delivered'">
                <span class="text-base font-bold">Déjà livré</span>
              </template>
              <template v-else-if="orderData?.status == 'arrived'">
                <span class="text-base font-bold">Livreur arrivé</span>
              </template>
              <template v-else-if="isPaymentFailed">
                <span class="text-sm font-bold">Paiement Échoué</span>
              </template>
              <template v-else-if="isOrderCancelled">
                <span class="text-sm font-bold">Annulée</span>
              </template>
              <template v-else-if="!isOrderPreparing">
                <span class="text-sm font-bold text-slate-400">
                  En attente
                  <span
                    v-if="orderData?.durationMin"
                    class="font-normal opacity-70"
                    >({{ orderData.durationMin }} min)</span
                  >
                </span>
              </template>
              <template v-else-if="timerExpired">
                <span class="text-base">Bientôt livré </span>
              </template>
              <template v-else>
                {{ remainingMinutes }}
                <span class="text-sm font-bold">min</span>
                {{ remainingSeconds.toString().padStart(2, '0') }}
                <span class="text-sm font-bold">s</span>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Review Card / Call to action when delivered -->
      <div
        v-if="canAddReview"
        class="liquidGlassCard p-5 rounded-3xl mb-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 flex items-center justify-between gap-4 shadow-sm"
      >
        <div class="flex items-center gap-3 min-w-0">
          <div
            class="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center shrink-0 text-amber-500"
          >
            <svg class="w-6 h-6 fill-current" viewBox="0 0 20 20">
              <path
                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
              />
            </svg>
          </div>
          <div>
            <div class="font-bold text-slate-800 text-sm">
              Votre avis compte !
            </div>
            <div class="text-xs text-slate-500">
              Partagez votre expérience sur cette commande
            </div>
          </div>
        </div>
        <button
          @click="openReviewDialog"
          class="px-4 py-2.5 bg-gradient-to-r from-primary to-orange-500 text-white font-bold text-xs rounded-xl shadow-md hover:opacity-90 transition-all shrink-0"
        >
          Ajouter un avis
        </button>
      </div>

      <!-- Existing Review Card -->
      <div
        v-else-if="existingReview"
        class="liquidGlassCard p-5 rounded-3xl mb-4 bg-white border border-slate-100"
      >
        <div class="flex items-center justify-between mb-2">
          <div class="text-sm font-bold text-slate-800">Votre avis</div>
          <div class="flex items-center gap-1 text-amber-500">
            <svg
              v-for="star in 5"
              :key="star"
              class="w-4 h-4"
              :class="
                star <= existingReview.rating
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-slate-200 fill-slate-200'
              "
              viewBox="0 0 20 20"
            >
              <path
                d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
              />
            </svg>
            <span class="text-xs font-bold text-slate-600 ml-1">
              {{ existingReview.rating }}/5
            </span>
          </div>
        </div>
        <div
          v-if="existingReview.comment"
          class="text-xs text-slate-600 italic"
        >
          « {{ existingReview.comment }} »
        </div>
      </div>

      <!-- Rider Card -->
      <div
        class="liquidGlassCard p-6 rounded-3xl mb-4 overflow-hidden relative"
      >
        <div class="flex items-center gap-4">
          <div
            class="w-16 h-16 rounded-2xl overflow-hidden bg-white border border-slate-100 shrink-0"
          >
            <ImgComponent
              :src="orderData?.delivery?.rider?.profilePicture"
              fallback="/images/rider-placeholder.png"
              class="w-full h-full object-cover"
            />
          </div>
          <div class="flex-1 min-w-0">
            <div
              class="text-xs text-primary font-bold uppercase tracking-wider mb-1"
            >
              Votre Livreur
            </div>
            <div class="text-lg font-bold title-color truncate">
              {{
                orderData?.delivery?.rider?.name ||
                orderData?.rider?.name ||
                'Pas encore assigné'
              }}
            </div>
            <div
              class="flex items-center gap-2 mt-1"
              v-if="orderData?.delivery?.rider?.name || orderData?.rider?.name"
            >
              <div class="flex items-center gap-1 text-yellow-500">
                <svg class="w-4 h-4 fill-current" viewBox="0 0 20 20">
                  <path
                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                  />
                </svg>
                <span class="text-sm font-bold">{{
                  orderData?.delivery?.rider?.rating ||
                  orderData?.rider?.rating ||
                  ''
                }}</span>
              </div>
              <span class="text-slate-300">•</span>
              <span class="text-sm text-slate-500">{{
                orderData?.delivery?.rider?.vehicle ||
                orderData?.rider?.vehicle ||
                'Moto'
              }}</span>
            </div>
          </div>
          <div class="flex gap-2">
            <a
              v-if="
                orderData?.delivery?.rider?.phone || orderData?.rider?.phone
              "
              :href="`tel:${orderData?.delivery?.rider?.phone || orderData?.rider?.phone}`"
              class="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary/20 transition-colors"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </a>
            <button
              @click="openChatWithRider"
              class="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary/20 transition-colors"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div class="border-t border-slate-100 pt-4 mt-4">
          <div class="text-lg font-semibold mb-2 title-color">
            Code de confirmation de livraison
          </div>
          <div
            v-if="orderData?.deliveryCode"
            class="text-sm font-semibold mb-2 text-red-500"
          >
            Ne donnez ce code au livreur que si vous avez reçu la livraison
          </div>
          <div
            class="text-xl font-bold opacity-80 title-color text-primary flex gap-1 items-center cursor-pointer"
            @click="copyDeliveryCode"
          >
            <span v-if="orderData?.deliveryCode">{{
              orderData.deliveryCode.includes('-')
                ? orderData.deliveryCode.split('-')[1]
                : orderData.deliveryCode
            }}</span>
            <span v-else> En attente du livreur </span>
            <div class="w-8 h-8" v-if="orderData?.deliveryCode">
              <ColorImg
                class="bg-primary w-full h-full"
                src="/images/all/copy.svg"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Products Card -->
      <div class="liquidGlassCard p-6 rounded-3xl mb-4">
        <div class="text-lg font-bold mb-4 title-color">Produits commandés</div>
        <div class="space-y-4">
          <!-- Products -->
          <div
            v-for="product in orderProducts"
            :key="product.id || product.dishId"
            class="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0"
          >
            <div
              class="w-16 h-16 rounded-2xl overflow-hidden bg-slate-50 border border-slate-100 shrink-0"
            >
              <img
                :src="
                  product.image ||
                  product.principalImageUrl ||
                  '/images/placeholder-dish.png'
                "
                class="w-full h-full object-cover"
                onerror="this.src = '/images/placeholder-dish.png'"
              />
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-bold text-slate-800 truncate">
                {{ product.title }}
              </div>
              <div
                class="text-xs text-slate-500 flex items-center gap-2 mt-0.5"
              >
                <span class="bg-slate-100 px-2 py-0.5 rounded-full font-medium"
                  >x{{ product.quantity || product.numberOrdered || 1 }}</span
                >
                <span>{{
                  orderData?.restaurantName || product.storeName
                }}</span>
              </div>
            </div>
            <div class="text-right shrink-0">
              <div class="font-bold text-slate-900">
                {{
                  formatPrice(
                    product.totalPrice ||
                      product.unitPrice * product.quantity ||
                      0
                  )
                }}
              </div>
            </div>
          </div>

          <!-- Global Drinks -->
          <div
            v-if="orderData?.drinks?.length > 0"
            class="pt-2 border-t border-slate-100"
          >
            <div
              class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3"
            >
              Boissons
            </div>
            <div
              v-for="(drink, index) in orderData.drinks"
              :key="'drink-' + index"
              class="flex items-center gap-4 py-2"
            >
              <div
                class="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"
              >
                <ColorImg
                  src="/images/icons/local-drink.svg"
                  class="w-6 h-6 opacity-70 bg-primary"
                />
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-bold text-slate-800 truncate">
                  {{ drink.name }}
                </div>
                <div class="text-xs text-slate-500">
                  {{ formatPrice(drink.price || 0) }} x
                  {{ drink.quantity || 1 }}
                </div>
              </div>
              <div class="text-right shrink-0 font-bold text-slate-900 text-sm">
                {{ formatPrice((drink.price || 0) * (drink.quantity || 1)) }}
              </div>
            </div>
          </div>
        </div>

        <div class="border-t border-slate-100 mt-6 pt-6 space-y-3">
          <div class="flex justify-between text-sm">
            <span class="text-slate-500">Sous-total</span>
            <span class="text-slate-800 font-medium">{{
              formatPrice(
                orderData?.pricing?.subtotal || orderData?.subtotal || 0
              )
            }}</span>
          </div>
          <div
            v-if="
              (orderData?.pricing?.deliveryFee || orderData?.deliveryFee) > 0
            "
            class="flex justify-between text-sm"
          >
            <span class="text-slate-500">Frais de livraison</span>
            <span class="text-slate-800 font-medium">{{
              formatPrice(
                orderData?.pricing?.deliveryFee || orderData?.deliveryFee || 0
              )
            }}</span>
          </div>
          <div
            v-if="(orderData?.pricing?.serviceFee || orderData?.serviceFee) > 0"
            class="flex justify-between text-sm"
          >
            <span class="text-slate-500">Frais de service</span>
            <span class="text-slate-800 font-medium">{{
              formatPrice(
                orderData?.pricing?.serviceFee || orderData?.serviceFee || 0
              )
            }}</span>
          </div>
          <div
            v-if="(orderData?.pricing?.discount || orderData?.discount) > 0"
            class="flex justify-between text-sm text-green-600 font-medium"
          >
            <span>Réduction</span>
            <span
              >-{{
                formatPrice(orderData?.pricing?.discount || orderData.discount)
              }}</span
            >
          </div>
          <div class="flex justify-between pt-3 border-t border-slate-100">
            <span class="text-lg font-bold text-slate-800">Total</span>
            <span class="text-xl font-black text-primary">{{
              formatPrice(
                orderData?.pricing?.total || orderData?.totalPrice || 0
              )
            }}</span>
          </div>
        </div>
      </div>

      <!-- Timeline -->
      <div
        class="liquidGlassCard flex flex-col mb-4 items-center rounded-3xl p-4"
      >
        <OrderStep
          v-if="
            orderData?.payment?.method !== 'cash' &&
            orderData?.paymentMethod !== 'cash'
          "
          icon="/images/all/money.svg"
          :title="paymentStepTitle"
          :description="paymentStepDescription"
          :active="getStepStatus('payment')"
          :is-failed="isPaymentFailed"
          :date="formatDate(getStepDate('payment'))"
          step-name="payment"
          :all-steps="allSteps"
          :timeline="orderData?.stepsTimeline"
        />
        <OrderStep
          icon="/images/all/restaurant.svg"
          :title="
            isOrderCancelled ? 'Commande annulée' : 'Accepté par le restaurant'
          "
          :description="
            isOrderCancelled
              ? 'La commande a été annulée ou rejetée'
              : 'Le restaurant a accepté votre commande'
          "
          :active="getStepStatus('accepted')"
          :is-cancelled="isOrderCancelled"
          :date="formatDate(getStepDate('accepted'))"
          step-name="accepted"
          :all-steps="allSteps"
          :timeline="orderData?.stepsTimeline"
        />
        <OrderStep
          icon="/images/all/restaurant.svg"
          title="Cuisine débutée"
          description="Votre repas est en cours de préparation"
          :active="getStepStatus('preparing')"
          :date="formatDate(getStepDate('preparing'))"
          step-name="preparing"
          :all-steps="allSteps"
          :timeline="orderData?.stepsTimeline"
        />
        <OrderStep
          icon="/images/all/dish.svg"
          title="Repas cuisiné"
          description="Votre commande est prête pour la livraison"
          :active="getStepStatus('cooked')"
          :date="formatDate(getStepDate('cooked'))"
          step-name="cooked"
          :all-steps="allSteps"
          :timeline="orderData?.stepsTimeline"
        />
        <OrderStep
          icon="/images/all/scooter.svg"
          title="Livraison en cours"
          description="Votre commande est en chemin"
          :active="getStepStatus('deliveryStarted')"
          :date="formatDate(getStepDate('deliveryStarted'))"
          step-name="deliveryStarted"
          :all-steps="allSteps"
          :timeline="orderData?.stepsTimeline"
        />

        <!-- Affichage de l'ETA quand le rider est en chemin -->
        <div
          v-if="
            getStepStatus('deliveryStarted') &&
            !getStepStatus('delivered') &&
            !isOrderCancelled &&
            etaMessage
          "
          class="w-full flex flex-col items-center justify-center p-4 mb-4 bg-primary/10 rounded-2xl border-2 border-primary border-dashed animate-pulse"
        >
          <div
            class="text-primary font-bold text-center uppercase tracking-wider mb-1"
          >
            {{ etaMessage.title }}
          </div>
          <div class="text-primary-600 text-xs text-center font-medium px-4">
            {{ etaMessage.subtitle }}
          </div>
        </div>

        <OrderStep
          icon="/images/all/home.svg"
          title="Commande livrée"
          description="Livrée à votre porte. Bon appétit !"
          :active="getStepStatus('delivered')"
          :date="formatDate(getStepDate('delivered'))"
          step-name="delivered"
          :all-steps="allSteps"
          :timeline="orderData?.stepsTimeline"
        />
      </div>

      <!-- Delivery tracking Card -->
      <div
        class="liquidGlassCard p-6 rounded-3xl mb-12 overflow-hidden relative"
      >
        <div class="">
          <div class="flex items-center gap-3 mb-4">
            <div
              class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"
            >
              <svg
                class="w-4 h-4 text-slate-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <div
              class="text-sm font-bold text-slate-800 uppercase tracking-wider"
            >
              Détails de la livraison
            </div>
          </div>
          <div class="space-y-3">
            <div class="flex gap-3">
              <div
                class="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0"
              ></div>
              <div class="text-sm">
                <span
                  class="text-slate-500 block text-xs font-bold uppercase tracking-tight mb-0.5"
                  >De</span
                >
                <span class="text-slate-800 font-medium">{{
                  orderData?.pickupLabel || orderData?.restaurantName || '-'
                }}</span>
              </div>
            </div>
            <div class="flex gap-3">
              <div
                class="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 shrink-0"
              ></div>
              <div class="text-sm">
                <span
                  class="text-slate-500 block text-xs font-bold uppercase tracking-tight mb-0.5"
                  >À</span
                >
                <span class="text-slate-800 font-medium">{{
                  orderData?.destinationLabel ||
                  orderData?.destinationAddress ||
                  orderData?.deliveryLocation?.address ||
                  '-'
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="border-t pt-4 border-slate-100">
          <div class="text-lg font-semibold mb-2 title-color">
            Partager le suivi
          </div>
          <div class="text-sm opacity-80 title-color mb-3">
            Partagez ce lien avec le destinataire pour qu'il puisse suivre la
            livraison en temps réel
          </div>
          <div class="flex gap-2">
            <button
              @click="copyTrackingLink"
              class="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                />
              </svg>
              Copier le lien
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import Map from '@features/map/Map.vue';
import OrderStep from './OrderStep.vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';
import Commons from '@features/shared/libraries/commons';

const props = defineProps({
  orderData: { type: Object, default: null }
});

const emit = defineEmits(['close']);

const mapRef = ref(null);
const onceLaunched = ref(false);

const remainingMinutes = ref(0);
const remainingSeconds = ref(0);
const timerExpired = ref(false);
let timerInterval = null;
let riderPollingInterval = null;

const riderLocation = ref(null);
let riderRouteTraced = false;

// Computed d'évaluation des paiements, échecs / annulations
const isPaymentPaid = computed(() => {
  const pStatus =
    props.orderData?.payment?.status || props.orderData?.paymentStatus;
  if (['paid', 'completed', 'successful', 'success'].includes(pStatus)) {
    return true;
  }
  const timeline = props.orderData?.timeline || [];
  return timeline.some((t) => ['payment_paid', 'paid'].includes(t.status));
});

const isPaymentFailed = computed(() => {
  const pStatus =
    props.orderData?.payment?.status || props.orderData?.paymentStatus;
  const oStatus = props.orderData?.status;
  return (
    ['failed', 'payment_failed', 'declined', 'expired'].includes(pStatus) ||
    oStatus === 'payment_failed'
  );
});

const paymentStepTitle = computed(() => {
  if (isPaymentFailed.value) return 'Paiement échoué';
  if (isPaymentPaid.value) return 'Paiement confirmé';
  return 'Paiement en attente';
});

const paymentStepDescription = computed(() => {
  if (isPaymentFailed.value) return "Le règlement n'a pas pu être effectué";
  if (isPaymentPaid.value) return 'Votre commande a été payée avec succès';
  return 'En attente de confirmation du règlement';
});

const isOrderCancelled = computed(() => {
  const oStatus = props.orderData?.status;
  return [
    'cancelled',
    'rejected',
    'canceled_by_user',
    'canceled_by_restaurant',
    'failed'
  ].includes(oStatus);
});

const failureReasonMessage = computed(() => {
  if (isPaymentFailed.value) {
    return "Le paiement de la commande n'a pas pu être finalisé.";
  }
  if (props.orderData?.cancelledBy) {
    return `La commande a été annulée par ${props.orderData.cancelledBy}.`;
  }
  return 'Cette commande a été annulée ou interrompue.';
});

const orderProducts = computed(() => {
  return props.orderData?.items || props.orderData?.products || [];
});

async function fetchRiderPosition() {
  if (!props.orderData || isOrderCancelled.value || isPaymentFailed.value)
    return;
  const status = props.orderData.status;
  if (
    [
      'delivered',
      'cancelled',
      'rejected',
      'canceled_by_user',
      'canceled_by_restaurant'
    ].includes(status)
  )
    return;

  const riderId =
    props.orderData.delivery?.rider?.riderId ||
    props.orderData.riderId ||
    props.orderData.delivery?.riderId;
  if (!riderId) return;

  try {
    const res = await fetch(
      `https://api.monrapidoo.com/rider/details/${riderId}?group=ultraxeno`
    );
    const json = await res.json();
    if (!json.data || !json.data.lat || !json.data.lng) return;

    riderLocation.value = {
      lat: json.data.lat,
      lng: json.data.lng
    };

    if (!riderRouteTraced) {
      riderRouteTraced = true;
      traceRoute();
    } else {
      updateRiderPosition();
    }
  } catch (error) {
    console.error('Erreur récupération position livreur:', error);
  }
}

const getPreparingAt = () => {
  const timeline = props.orderData?.timeline || [];
  const entry = timeline.find((t) => t.status === 'preparing');
  return entry?.timestamp || null;
};

const isOrderPreparing = computed(() => {
  const preparingStatuses = [
    'preparing',
    'ready_for_pickup',
    'picked_up',
    'arrived',
    'delivered'
  ];
  return preparingStatuses.includes(props.orderData?.status);
});

const updateTimer = () => {
  if (!props.orderData || isOrderCancelled.value || isPaymentFailed.value)
    return;

  const totalMin =
    props.orderData.durationMin ||
    (props.orderData.estimatedCookingTimeMin || 0) +
      (props.orderData.deliveryDurationMin || 0);
  if (totalMin === 0) {
    remainingMinutes.value = 0;
    remainingSeconds.value = 0;
    return;
  }

  const preparingAt = getPreparingAt();
  const startMs = preparingAt
    ? new Date(preparingAt).getTime()
    : props.orderData.createdAt
      ? new Date(props.orderData.createdAt).getTime()
      : Date.now();
  const targetTimeMs = startMs + totalMin * 60 * 1000;
  const diffMs = targetTimeMs - Date.now();

  if (diffMs <= 0) {
    timerExpired.value = true;
    remainingMinutes.value = 0;
    remainingSeconds.value = 0;
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
    return;
  }

  timerExpired.value = false;
  remainingMinutes.value = Math.floor(diffMs / 60000);
  remainingSeconds.value = Math.floor((diffMs % 60000) / 1000);
};

watch(
  () => props.orderData,
  () => {
    updateTimer();
  },
  { deep: true }
);

const etaMessage = computed(() => {
  const durationRaw =
    props.orderData?.durationRemaining ||
    props.orderData?.delivery?.durationRemaining;
  let duration = null;

  if (typeof durationRaw === 'number') {
    duration = durationRaw;
  } else if (typeof durationRaw === 'string') {
    const match = durationRaw.match(/(\d+)/);
    if (match) duration = parseInt(match[1]);
  }

  if (duration === null) return null;

  if (duration <= 2) {
    return {
      title: 'RIDER EST LÀ SORTEZ PRENDRE VOTRE COMMANDE',
      subtitle: "Votre livreur vient d'arriver à votre destination"
    };
  } else {
    return {
      title: `RIDER ARRIVE DANS ${duration}MIN APPRETÉ VOUS À RECEVOIR VOTRE COMMANDE`,
      subtitle: 'Suivez son avancée en temps réel sur la carte ci-dessus'
    };
  }
});

const allSteps = [
  'payment',
  'accepted',
  'preparing',
  'cooked',
  'deliveryStarted',
  'delivered'
];

onMounted(() => {
  updateTimer();
  timerInterval = setInterval(updateTimer, 1000);
  riderPollingInterval = setInterval(fetchRiderPosition, 10000);
});

onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval);
  if (riderPollingInterval) clearInterval(riderPollingInterval);
});

async function onMapReady() {
  await fetchRiderPosition();
  if (!riderRouteTraced) {
    riderRouteTraced = true;
    traceRoute();
  }
}

function traceRoute() {
  onceLaunched.value = true;
  if (!mapRef.value || !props.orderData) return;

  const pickupLat =
    parseFloat(
      props.orderData.pickupLat ||
        props.orderData.metadata?.restaurantLocation?.lat
    ) || 6.3571;
  const pickupLng =
    parseFloat(
      props.orderData.pickupLng ||
        props.orderData.metadata?.restaurantLocation?.lng
    ) || 2.3699;
  const destinationLat =
    parseFloat(
      props.orderData.destinationLat || props.orderData.deliveryLocation?.lat
    ) || 6.3832;
  const destinationLng =
    parseFloat(
      props.orderData.destinationLng || props.orderData.deliveryLocation?.lng
    ) || 2.4156;

  let startPoint;
  let endPoint = {
    lat: destinationLat,
    lng: destinationLng,
    content: 'Destination',
    iconType: 'destination'
  };
  let checkpoints = [];

  const restaurantPoint = {
    lat: pickupLat,
    lng: pickupLng,
    content: 'Restaurant',
    iconType: 'restaurant'
  };

  if (riderLocation.value) {
    const riderPoint = {
      lat: riderLocation.value.lat,
      lng: riderLocation.value.lng,
      content: 'Votre Livreur',
      iconType: 'rider'
    };

    const status = props.orderData.status;
    const riderPickedUp = ['picked_up', 'arrived', 'in_transit'].includes(
      status
    );

    if (riderPickedUp) {
      startPoint = restaurantPoint;
      checkpoints = [riderPoint];
    } else {
      startPoint = riderPoint;
      checkpoints = [restaurantPoint];
    }
  } else {
    startPoint = restaurantPoint;
    checkpoints = (props.orderData.checkpoints || []).map((cp, i) => ({
      lat: parseFloat(cp.lat || cp.coordinates?.lat) || 6.3701,
      lng: parseFloat(cp.lng || cp.coordinates?.lng) || 2.3923,
      content: `Point de passage ${i + 1}`,
      iconType: 'checkpoint'
    }));
  }

  mapRef.value.traceRouteWithPoints(
    'order-route',
    startPoint,
    endPoint,
    checkpoints
  );

  updateRiderPosition();
}

function updateRiderPosition() {
  if (!riderLocation.value) return;
  mapRef.value?.setMarker(
    {
      position: {
        lat: riderLocation.value.lat,
        lng: riderLocation.value.lng
      },
      content: 'Votre Livreur',
      iconType: 'rider'
    },
    'order-route',
    'rider-position'
  );
}

function formatPrice(amount) {
  if (!amount) return '0 FCFA';
  return (
    new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0
    }).format(amount) + ' FCFA'
  );
}

function formatDate(timestamp) {
  if (!timestamp) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(timestamp));
}

function getStepDate(step) {
  if (!props.orderData) return null;

  if (step === 'payment' && !isPaymentPaid.value) {
    return null;
  }

  // 1. Chercher dans stepsTimeline s'il existe
  if (props.orderData.stepsTimeline?.[step]) {
    return props.orderData.stepsTimeline[step];
  }

  // 2. Chercher dans le tableau timeline provenant du JSON
  const timeline = props.orderData.timeline || [];
  const mapStepToTimelineStatus = {
    payment: ['payment_paid', 'paid'],
    accepted: ['accepted'],
    preparing: ['preparing'],
    cooked: ['ready_for_pickup'],
    deliveryStarted: ['picked_up', 'in_transit', 'arrived'],
    delivered: ['delivered']
  };

  const matchedStatuses = mapStepToTimelineStatus[step] || [];
  const entry = timeline.find((t) => matchedStatuses.includes(t.status));
  return entry?.timestamp || null;
}

function getStepStatus(step) {
  if (step === 'payment') {
    if (isPaymentFailed.value) return false;
    return isPaymentPaid.value;
  }

  if (isOrderCancelled.value) return false;

  const timeline = props.orderData?.stepsTimeline || {};
  const status = props.orderData?.status || '';

  const currentStepIndex = getStatusStepIndex(status);

  switch (step) {
    case 'accepted':
      return (
        !!timeline.accepted ||
        !!getStepDate('accepted') ||
        currentStepIndex >= 1
      );
    case 'preparing':
      return (
        !!timeline.preparing ||
        !!getStepDate('preparing') ||
        currentStepIndex >= 2
      );
    case 'cooked':
      return (
        !!timeline.cooked || !!getStepDate('cooked') || currentStepIndex >= 3
      );
    case 'deliveryStarted':
      return (
        !!timeline.deliveryStarted ||
        !!getStepDate('deliveryStarted') ||
        currentStepIndex >= 4
      );
    case 'delivered':
      return (
        !!timeline.delivered ||
        !!getStepDate('delivered') ||
        status === 'delivered'
      );
    default:
      return false;
  }
}

function getStatusStepIndex(status) {
  const statusMap = {
    pending: 0,
    confirmed: 0,
    accepted: 1,
    preparing: 2,
    ready_for_pickup: 3,
    picked_up: 4,
    arrived: 4,
    in_transit: 4,
    delivered: 5,
    cancelled: -1,
    failed: -1
  };
  return statusMap[status] ?? -1;
}

function copyDeliveryCode() {
  if (!props.orderData?.deliveryCode) return;
  const code = props.orderData.deliveryCode.includes('-')
    ? props.orderData.deliveryCode.split('-')[1]
    : props.orderData.deliveryCode;
  Commons.copyToClipboard(
    code,
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'success',
        title: 'Copié',
        body: 'Code copié dans le presse-papier'
      });
    },
    (err) => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'error',
        title: 'Erreur de copie',
        body: 'Impossible de copier le code'
      });
      console.log(err);
    }
  );
}

function copyTrackingLink() {
  if (!props.orderData?.id) return;
  const baseUrl =
    window.$constants?.URLS?.orderTrackingBaseUrl || window.location.origin;
  const link = `${baseUrl}/track/order/${props.orderData.id}`;
  Commons.copyToClipboard(
    link,
    () => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'success',
        title: 'Lien copié',
        body: 'Lien de tracking copié dans le presse-papier'
      });
    },
    (err) => {
      bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
        type: 'error',
        title: 'Erreur de copie',
        body: 'Impossible de copier le lien de tracking'
      });
      console.log(err);
    }
  );
}

function closeDialog() {
  emit('close');
}

function openChatWithRider() {
  const riderName =
    props.orderData?.delivery?.rider?.name || props.orderData?.rider?.name;
  if (!riderName) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Rider non assigné',
      body: "Aucun rider n'a été assigné à votre commande pour le moment"
    });
    return;
  }
  bus.emit('chat.open_with_rider', props.orderData);
}

// Review helpers
const existingReview = computed(() => {
  if (props.orderData?.review?.rating) {
    return {
      rating: props.orderData.review.rating,
      comment: props.orderData.review.comment
    };
  }
  if (props.orderData?.rating) {
    return {
      rating: props.orderData.rating,
      comment: props.orderData.comment || props.orderData.reviewComment || ''
    };
  }
  return null;
});

const canAddReview = computed(() => {
  return props.orderData?.status === 'delivered' && !existingReview.value;
});

function openReviewDialog() {
  if (!props.orderData) return;
  bus.emit(
    constants.EVENTS.ORDERS.REVIEW_REQUIRED || 'orders.review_required',
    props.orderData
  );
}

// Auto open review dialog when order transitions to 'delivered' via periodic refresh
const hasAutoOpenedReview = ref(false);
const previousStatus = ref(props.orderData?.status);

watch(
  () => props.orderData?.id,
  (newId, oldId) => {
    if (newId !== oldId) {
      hasAutoOpenedReview.value = false;
      previousStatus.value = props.orderData?.status;
    }
  }
);

watch(
  () => props.orderData?.status,
  (newStatus, oldStatus) => {
    const prev = oldStatus !== undefined ? oldStatus : previousStatus.value;
    if (
      prev &&
      prev !== 'delivered' &&
      newStatus === 'delivered' &&
      !hasAutoOpenedReview.value &&
      canAddReview.value
    ) {
      hasAutoOpenedReview.value = true;
      openReviewDialog();
    }
    previousStatus.value = newStatus;
  }
);
</script>
